/**
 * Skeleton de carregamento do marketplace.
 *
 * Cada variante desenha o ESQUELETO DA PÁGINA REAL: usa os MESMOS containers
 * (`MpProduct-hero`, `MpCart-grid`, `MpOrderDetail-grid`…) do conteúdo que vai
 * substituir, trocando só as folhas por blocos de shimmer. Como o grid, o gap e
 * o padding são os mesmos, cada bloco nasce no tamanho e na POSIÇÃO em que o
 * conteúdo vai aparecer — a troca não empurra nada na tela.
 *
 * Por isso não existe "skeleton genérico bonito": ao criar uma variante nova,
 * copie a árvore de containers da view e substitua texto/mídia por `MpSkel`.
 *
 *   <MpSkeleton variant="product" />   detalhe do produto (hero + 2 colunas)
 *   <MpSkeleton variant="cart" />      carrinho (itens + resumo)
 *   <MpSkeleton variant="checkout" />  checkout (form + resumo)
 *   <MpSkeleton variant="order" />     detalhe do pedido
 *   <MpSkeleton variant="store" />     painel do vendedor
 *   <MpSkeleton variant="cards" />     grade da loja
 *   <MpSkeleton variant="list" />      lista de linhas
 *   <MpSkeleton variant="detail" />    cartão de detalhe simples (fallback)
 */
export default class MpSkeleton {
  view(vnode) {
    const variant = vnode.attrs.variant || 'list';
    const count = vnode.attrs.count ?? this.defaultCount(variant);
    const frame = vnode.attrs.frame !== false;
    const composer = vnode.attrs.composer === true;

    // aria-hidden: é placeholder visual; quem usa leitor de tela ouve o estado
    // de carregamento da página, não a árvore de blocos vazios.
    return m("div", {
      className: 'MpSkel-wrap MpSkel-wrap--' + variant,
      "aria-hidden": "true"
    }, this.renderVariant(variant, count, frame, composer));
  }
  defaultCount(variant) {
    if (variant === 'cards') return 6;
    if (variant === 'list') return 5;
    if (variant === 'cart') return 3;
    if (variant === 'order') return 2;
    return 1;
  }
  renderVariant(variant, count, frame, composer) {
    switch (variant) {
      case 'product':
        return this.product(composer);
      case 'cart':
        return this.cart(count);
      case 'checkout':
        return this.checkout();
      case 'order':
        return this.order(count);
      case 'store':
        return this.store();
      case 'bundle':
        return this.bundle(count);
      case 'course':
        return this.course(count);
      case 'seller':
        return this.seller(count);
      case 'cards':
        return this.cards(count);
      case 'detail':
        return this.detail(frame);
      default:
        return this.list(count);
    }
  }

  /**
   * Empilha blocos irmãos sem criar caixa: o wrapper é `display: contents`,
   * então o layout é idêntico ao de devolver os elementos soltos — e sem
   * fragmento com key (o Mithril quebra quando irmãos misturam keyed/unkeyed).
   */
  stack() {
    for (var _len = arguments.length, children = new Array(_len), _key = 0; _key < _len; _key++) {
      children[_key] = arguments[_key];
    }
    return m("div", {
      className: "MpSkel-stack"
    }, children);
  }

  /** Bloco de shimmer. `mods` são sufixos de `MpSkel--`. */
  b() {
    for (var _len2 = arguments.length, mods = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      mods[_key2] = arguments[_key2];
    }
    return m("div", {
      className: ['MpSkel', ...mods.map(mod => 'MpSkel--' + mod)].join(' ')
    });
  }
  repeat(n, render) {
    return Array.from({
      length: n
    }).map((_, i) => render(i));
  }
  chips(n) {
    return m("div", {
      className: "MpSkel-chips"
    }, this.repeat(n, i => this.b('chip', i % 2 ? 'chip-sm' : 'chip-md')));
  }

  /** Linha "rótulo à esquerda, valor à direita" — resumos, totais, info cards. */
  pair(short) {
    if (short === void 0) {
      short = false;
    }
    return m("div", {
      className: "MpSkel-pair"
    }, this.b('line', short ? 'w-30' : 'w-40'), this.b('line', 'w-20'));
  }

  // ── Produto: hero (ícone 88 + título + chips | preço + CTA) + 2 colunas ──
  product(composer) {
    return this.stack(this.b('back'), m("section", {
      className: "MpProduct-hero"
    }, m("div", {
      className: "MpProduct-heroLeft"
    }, this.b('productIcon'), m("div", {
      className: "MpProduct-heroMeta"
    }, this.b('title'), this.b('line', 'w-40'), this.chips(3))), m("div", {
      className: "MpProduct-heroRight"
    }, this.b('price'), this.b('line', 'w-50'), this.b('cta'))),
    // Banda do `composer require`: fica entre o hero e as abas nos produtos
    // entregues por pacote.
    composer ? m("div", {
      className: "MpProduct-composerHint"
    }, m("div", {
      className: "MpSkel-composerText"
    }, this.b('cardIcon'), m("div", {
      className: "MpSkel-composerLines"
    }, this.b('line', 'tx-md'), this.b('line', 'w-70'))), this.b('command')) : null, m("div", {
      className: "MpProduct-cols"
    }, m("main", {
      className: "MpProduct-main"
    }, m("div", {
      className: "MpSkel-tabs"
    }, this.repeat(4, i => this.b('tab', i === 3 ? 'w-30' : 'w-20'))), m("div", {
      className: "MpProduct-section"
    }, this.b('title', 'w-30'), this.b('line'), this.b('line'), this.b('line', 'w-60')), m("div", {
      className: "MpProduct-section"
    }, this.b('title', 'w-20'), this.b('gallery'))), m("aside", {
      className: "MpProduct-sidebar"
    }, m("div", {
      className: "MpProduct-card"
    }, this.b('title', 'w-50'), this.pair(), this.pair(), this.pair(true)), m("div", {
      className: "MpProduct-card"
    }, this.b('title', 'w-40'), this.pair(), this.pair(true)))));
  }

  // ── Carrinho: itens (ícone 64 + nome/meta + qtd + preço) + resumo ──
  cart(count) {
    return m("div", {
      className: "MpCart-grid"
    }, m("main", {
      className: "MpCart-main"
    }, m("div", {
      className: "MpCart-items"
    }, this.repeat(count, i => m("div", {
      key: String(i),
      className: "MpSkel-cartItem"
    }, this.b('cartIcon'), m("div", {
      className: "MpSkel-cartBody"
    }, this.b('line', 'w-60'), this.b('line', 'w-30')), this.b('qty'), this.b('amount'))))), m("aside", {
      className: "MpCart-side"
    }, m("div", {
      className: "MpSkel-card"
    }, this.b('title', 'w-50'), this.pair(), this.pair(true), this.b('total'), this.b('cta'))));
  }

  // ── Checkout: seções numeradas do form + resumo lateral ──
  checkout() {
    return m("div", {
      className: "MpCheckout-form"
    }, m("main", {
      className: "MpCheckout-main"
    }, this.repeat(3, i => m("div", {
      key: String(i),
      className: "MpSkel-card"
    }, m("div", {
      className: "MpSkel-sectionHead"
    }, this.b('num'), this.b('title', 'w-40')), this.b('field'), m("div", {
      className: "MpSkel-fieldRow"
    }, this.b('field'), this.b('field'))))), m("aside", {
      className: "MpCheckout-side"
    }, m("div", {
      className: "MpSkel-card"
    }, this.b('title', 'w-50'), this.pair(), this.pair(), this.b('total'), this.b('cta'))));
  }

  // ── Detalhe do pedido: hero (ref + status + total) + itens + resumo ──
  order(count) {
    return this.stack(this.b('back'), m("section", {
      className: "MpSkel-orderHero"
    }, m("div", {
      className: "MpSkel-orderHeroMain"
    }, this.b('title', 'w-40'), this.b('line', 'w-30')), m("div", {
      className: "MpSkel-orderHeroSide"
    }, this.b('chip', 'chip-md'), this.b('price'))), m("div", {
      className: "MpOrderDetail-grid"
    }, m("main", {
      className: "MpOrderDetail-main"
    }, m("div", {
      className: "MpSkel-card"
    }, this.b('title', 'w-30'), this.repeat(count, i => m("div", {
      key: String(i),
      className: "MpSkel-orderItem"
    }, this.b('cartIcon'), m("div", {
      className: "MpSkel-cartBody"
    }, this.b('line', 'w-60'), this.b('line', 'w-30')), this.b('amount'))))), m("aside", {
      className: "MpOrderDetail-side"
    }, m("div", {
      className: "MpSkel-card"
    }, this.b('title', 'w-50'), this.pair(), this.pair(), this.b('total')))));
  }

  // ── Painel do vendedor: banda de status + analytics + form da loja ──
  store() {
    return this.stack(m("div", {
      className: "MpSkel-card MpSkel-card--band"
    }, m("div", {
      className: "MpSkel-pair"
    }, this.b('line', 'w-20'), this.b('chip', 'chip-md'))), m("div", {
      className: "MpSkel-card"
    }, this.b('title', 'w-30'), m("div", {
      className: "MpSkel-statGrid"
    }, this.repeat(6, () => this.b('stat')))), m("div", {
      className: "MpSkel-card"
    }, this.b('title', 'w-40'), m("div", {
      className: "MpSkel-logoRow"
    }, this.b('logo'), this.b('cta', 'w-30')), this.b('field'), this.b('textarea'), this.b('cta', 'w-30')));
  }

  // ── Bundle: hero (capa 220 + chips/título/preço) + produtos do pacote ──
  bundle(count) {
    return this.stack(this.b('back'), m("header", {
      className: "MpBundle-hero"
    }, this.b('bundleIcon'), m("div", {
      className: "MpBundle-heroBody"
    }, this.chips(2), this.b('title', 'w-60'), this.b('line'), this.b('line', 'w-50'), this.b('price'), this.b('cta', 'w-50'))), m("div", {
      className: "MpSkel-list"
    }, this.repeat(count + 2, i => m("div", {
      key: String(i),
      className: "MpSkel-row"
    }, this.b('cardIcon'), m("div", {
      className: "MpSkel-rowText"
    }, this.b('line', 'w-50'), this.b('line', 'w-30')), this.b('amount')))));
  }

  // ── Curso: banner + workspace (aula à esquerda, lista de aulas à direita) ──
  course(count) {
    return this.stack(m("div", {
      className: "MpSkel-courseBanner"
    }, this.b('line', 'w-30'), this.b('title', 'w-50'), this.chips(3)), m("div", {
      className: "MpCoursePlayer-workspace"
    }, m("div", {
      className: "MpCoursePlayer-left"
    }, this.b('video'), m("div", {
      className: "MpSkel-card"
    }, this.b('title', 'w-50'), this.b('line'), this.b('line', 'w-60'))), m("aside", {
      className: "MpCoursePlayer-right"
    }, m("div", {
      className: "MpSkel-card"
    }, this.b('title', 'w-50'), this.repeat(count + 3, i => m("div", {
      key: String(i),
      className: "MpSkel-pair"
    }, this.b('line', 'w-60'), this.b('chip', 'chip-sm')))))));
  }

  // ── Grade da loja: espelha o MpShop-card (ícone + título + chips + preço) ──
  cards(count) {
    return m("div", {
      className: "MpShop-grid"
    }, this.repeat(count, i => m("div", {
      key: String(i),
      className: "MpShop-card MpShop-card--skel"
    }, m("div", {
      className: "MpShop-cardHead"
    }, this.b('cardIcon'), m("div", {
      className: "MpShop-cardMain"
    }, this.b('line', 'w-70'), this.chips(2)), this.b('pricePill')), this.b('line'), this.b('line', 'w-50'), m("div", {
      className: "MpShop-cardFoot"
    }, this.b('line', 'tx-md'), this.b('line', 'tx-sm')))));
  }

  // ── Lista genérica: linha com ícone + duas linhas de texto ──
  list(count) {
    return m("div", {
      className: "MpSkel-list"
    }, this.repeat(count, i => m("div", {
      key: String(i),
      className: "MpSkel-row"
    }, this.b('avatar'), m("div", {
      className: "MpSkel-rowText"
    }, this.b('line', 'w-60'), this.b('line', 'w-30')), this.b('amount'))));
  }

  // ── Perfil da loja: identidade centralizada + statline + grade ──
  seller(count) {
    return this.stack(m("header", {
      className: "MpSkel-sellerId"
    }, this.b('logo', 'centered'), this.b('title', 'w-40', 'centered'), this.b('line', 'w-30', 'centered'), m("div", {
      className: "MpSkel-chips MpSkel-chips--centered"
    }, this.repeat(3, () => this.b('chip', 'chip-sm')))), m("div", {
      className: "MpSellerProfile-grid"
    }, this.repeat(count + 2, i => m("div", {
      key: String(i),
      className: "MpSellerProfile-card"
    }, m("div", {
      className: "MpSellerProfile-cover"
    }, this.b('fill')), m("div", {
      className: "MpSellerProfile-cardBody"
    }, this.b('line', 'w-70'), this.b('line', 'w-40'))))));
  }

  // ── Fallback: detalhe simples (cabeçalho + parágrafo + ação) ──
  detail(frame) {
    const body = this.stack(m("div", {
      className: "MpSkel-sectionHead"
    }, this.b('avatar'), this.b('title', 'w-50')), this.b('line'), this.b('line'), this.b('line', 'w-60'), this.b('cta', 'w-40'));
    return m("div", {
      className: "MpSkel-detail"
    }, frame ? m("div", {
      className: "MpSkel-card"
    }, body) : body);
  }
}
flarum.reg.add('ramon-marketplace', 'forum/components/MpSkeleton', MpSkeleton);